/**
 * `defer-templates-marketplace` v0 — public landing-page mirror of
 * the curated marketplace.
 *
 * Locked decision #5: "Landing-page mirror — same JSON artefact.
 * `/marketplace` route renders against `marketplace.json` shipped
 * with the landing-page bundle."
 *
 * We import the curated catalogue directly from
 * `@rynora/flow-templates-curated` — no API call, no auth, fully
 * static. Anonymous viewers see the same 5 cards but the per-card
 * CTA reads "Sign up to use" and points to `/register?returnTo=…`
 * so post-registration the user lands back on the marketplace tab
 * with the right card selected (covers MKT-04 from the regression
 * coverage table).
 */

import { Link, useNavigate } from 'react-router-dom';
import { BrandMark } from '@rynora/theme';
import { ArrowLeft, Library, LogIn, ShoppingCart, Search, UserPlus, Mail, type LucideIcon } from 'lucide-react';
import { CURATED_TEMPLATES } from '@rynora/flow-templates-curated';
import { MARKETPLACE, MARKETPLACE_PERSONA_LABEL } from '@rynora/messages';
import type { MarketplacePersona } from '@rynora/contracts';
import '../../legal/legal.css';

const PERSONA_ICONS: Record<MarketplacePersona, LucideIcon> = {
  login: LogIn,
  checkout: ShoppingCart,
  'search-and-filter': Search,
  'signup-with-email-verification': UserPlus,
  'contact-form': Mail,
};

export function MarketplacePage() {
  const navigate = useNavigate();

  const onSignUp = (templateId: string): void => {
    // Forward the templateId via `returnTo` so the in-app login redirect
    // can deep-link back to the marketplace tab with the same card
    // pre-selected. The flow-builder LoginPage already honours the
    // `returnTo` query param.
    const returnTo = encodeURIComponent(`/marketplace?templateId=${templateId}`);
    navigate(`/register?returnTo=${returnTo}`);
  };

  return (
    <div className="legal-shell">
      <header className="legal-header">
        <Link to="/" className="legal-back-link" aria-label="Back to home">
          <ArrowLeft size={16} aria-hidden="true" />
          <span>Home</span>
        </Link>
        <BrandMark />
      </header>
      <main className="legal-main marketplace-public">
        <header className="marketplace-public__hero">
          <Library size={28} aria-hidden="true" />
          <h1>{MARKETPLACE.landingTitle}</h1>
          <p>{MARKETPLACE.landingSubtitle}</p>
        </header>
        <section
          className="marketplace-public__grid"
          role="list"
          aria-label="Curated marketplace templates"
          data-testid="marketplace-public-grid"
        >
          {CURATED_TEMPLATES.map((template) => {
            const Icon = PERSONA_ICONS[template.persona];
            return (
              <article
                key={template.id}
                role="listitem"
                className="marketplace-public__card"
                data-testid={`marketplace-public-card-${template.id}`}
              >
                <div className="marketplace-public__card-icon" aria-hidden="true">
                  <Icon size={20} />
                </div>
                <div className="marketplace-public__card-body">
                  <header className="marketplace-public__card-header">
                    <h2>{template.name}</h2>
                    <span className="marketplace-public__card-badge" data-persona={template.persona}>
                      {MARKETPLACE_PERSONA_LABEL[template.persona]}
                    </span>
                  </header>
                  <p>{template.shortDescription}</p>
                  <p className="marketplace-public__card-long">{template.longDescription}</p>
                  <div className="marketplace-public__card-meta">
                    <span>{template.payload.steps.length} steps</span>
                    <span>{template.payload.mode}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => onSignUp(template.id)}
                    data-testid={`marketplace-public-signup-${template.id}`}
                  >
                    {MARKETPLACE.landingSignUpCta}
                  </button>
                </div>
              </article>
            );
          })}
        </section>
        <footer className="marketplace-public__footer">
          <p>{MARKETPLACE.landingMore}</p>
        </footer>
      </main>
    </div>
  );
}
